import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ViewStackService } from '../../../../shared/services/view-stack/view-stack.service';
import { formsActions } from '../forms-actions';
import { debounceTime, filter, map, tap } from 'rxjs';
import { ViewStackItem } from '../../../../shared/classes/view-stack-item';
import { ViewStackFormComponent } from '../../../../features/bizzmine/form/components/view-stack-form/view-stack-form.component';
import { viewStackActions } from '../view-stack-actions';
import { ViewStackLoaderType } from '../../../../shared/enums/view-stack-event-type.enum';
import { formsFeature } from '../forms-feature';
import { Store } from '@ngrx/store';

@Injectable()
export class FormsViewStackEffects {
  private actions$ = inject(Actions);
  private store$ = inject(Store);

  public showLoaderSkeletonActions$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.getFormInstanceById,
      formsActions.saveFormInstance,
      formsActions.saveGridLinkedFormInstance,
      formsActions.getTaskForm,
      formsActions.getAnonymousForm,
      formsActions.getFormByCollectionId,
      formsActions.getFormByCollectionField,
      formsActions.getFormByCollectionMethodType,
      formsActions.getFormCreate,
      formsActions.getFormByCollectionGridField,
      formsActions.getFormEdit,
      formsActions.getFormReadOnly,
      formsActions.getFormEdit,
      formsActions.getFormByWidgetId,
      formsActions.getLinkedForm,
      formsActions.getLinkedInstance,
      formsActions.getTaskOnBehalfOf
      ),
    map(() => ({
      type: viewStackActions.showLoadingState.type,
        loaderType: ViewStackLoaderType.SKELETON
    })
    )
  ));
  public showLoaderDefaultActions$ = createEffect(() => this.actions$.pipe(
    ofType(
      formsActions.deleteSelfLock,
      formsActions.deleteLock,
      formsActions.refreshForm,
      formsActions.saveFormInstance,
      formsActions.createFormInstance,
      formsActions.saveTask
    ),
    map(() => ({
        type: viewStackActions.showLoadingState.type,
        loaderType: ViewStackLoaderType.DEFAULT
      })
    )
  ));
  
  public removeLoaderFromLatestFormActions$ = createEffect(() => this.actions$.pipe(
    ofType(
      formsActions.failedFetch,
    ),
    map(() => ({
      type: viewStackActions.removeLoadingStateWithLatestForm.type
    }))
  ));

  public removeLoaderActions$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.formFetched,
      formsActions.lookupFailed,
      formsActions.gridLookupFailed,
      formsActions.saveFormInstanceSucceeded,
      formsActions.createFormInstanceSucceeded,
      formsActions.executeTaskSucceeded,
      formsActions.getEmptyRowForGridSucceeded,
      formsActions.formValidationSucceeded,
      formsActions.saveFormInstanceFailed,
      formsActions.createFormInstanceFailed,
      formsActions.executeTaskFailed,
      formsActions.getEmptyRowForGridFailed,
      formsActions.formValidationFailed,
      formsActions.saveGridLinkedFormInstanceSucceeded,
      formsActions.selfLockDeleted,
      formsActions.lockDeleted,
      formsActions.formRefreshed,
      formsActions.getFormFailed,
      formsActions.formClosed,
      formsActions.preValidationFailed,
      formsActions.failedFetch,
      formsActions.unAuthorizedFetch,
      viewStackActions.addFormToViewStack
    ),
    filter(action => action.type != formsActions.formFetched.type || action['addToViewStack'] == true),
    map(() => ({
      type: viewStackActions.removeLoadingState.type
    }))
  ));

  private viewStackService = inject(ViewStackService);
  public addFormToViewStack$ = createEffect(() => this.actions$.pipe(
    ofType(viewStackActions.addFormToViewStack),
    tap(({ data }) => {
      this.viewStackService.addItemToStack(new ViewStackItem(ViewStackFormComponent, data.id));
    })
  ), { dispatch: false });
  public removeFormFromViewStack$ = createEffect(() => this.actions$.pipe(
    ofType(viewStackActions.removeFormFromViewStack),
    tap(() => {
      this.viewStackService.removeLastItemFromStack();
    })
  ), { dispatch: false });

  public removeAllFormsFromViewStack$ = createEffect(() => this.actions$.pipe(
    ofType(viewStackActions.removeAllFormsFromViewStack),
    tap(() => {
      this.viewStackService.clearViewStack();
    })
  ), { dispatch: false });

  public showLoadingState$ = createEffect(() => this.actions$.pipe(
    ofType(viewStackActions.showLoadingState),
    tap(({loaderType}) => {
      this.viewStackService.setLoadingState(loaderType ?? ViewStackLoaderType.DEFAULT);
    })
  ), { dispatch: false });

  public removeLoadingState$ = createEffect(() => this.actions$.pipe(
    ofType(viewStackActions.removeLoadingState),
    tap(() => {
      this.viewStackService.removeLoadingState();
    })
  ), { dispatch: false });

  public removeLoadingStateWithLatestForm$ = createEffect(() => this.actions$.pipe(
    ofType(viewStackActions.removeLoadingStateWithLatestForm),
    tap(() => {
      const formIds = this.store$.selectSignal(formsFeature.selectIds)();
      if(formIds != undefined && formIds.length > 0) {
       const latestFormId = formIds[formIds.length - 1];
       this.store$.dispatch(formsActions.removeFormLoadingState({ formId: <string>latestFormId }));
      }
    }),
    map(() => viewStackActions.removeLoadingState())
  ));
}